/** @jsxImportSource theme-ui */
import { Box, Flex, Spinner, Text } from 'theme-ui';
import { Table } from '@component-controls/components';
import { useEffect, useState } from 'react';
import SearchBy from '@/components/SearchBy';
import Button from '@/components/Button';
import { useGetCustomersQuery } from '@/store/apiService';
import { customerColumns } from './columns';

const PAGE_SIZE = 50;

// GET /superadmin/customers matches by email prefix across every company (email is not
// unique across tenants, so the same person can appear once per company). An empty search
// intentionally returns nothing, so the list stays hidden until the admin types a prefix.
const Customers: React.FC = () => {
    const [email, setEmail] = useState('');
    const [take, setTake] = useState(PAGE_SIZE);

    // Reset paging whenever the search term changes so a new query starts from the first page.
    useEffect(() => {
        setTake(PAGE_SIZE);
    }, [email]);

    const trimmed = email.trim();
    const query = useGetCustomersQuery(
        { Email: trimmed, Take: take },
        { skip: trimmed.length === 0 }
    );

    const rows = query.data?.Results ?? [];
    const total = query.data?.Total ?? 0;
    const hasMore = rows.length < total;

    return (
        <Flex sx={{ flexDirection: 'column', gap: 2 }}>
            <Box sx={{ maxWidth: 480 }}>
                <SearchBy onSearch={setEmail} placeholder="Search customers by email..." />
            </Box>

            {trimmed.length === 0 ? (
                <Text>Type an email prefix above to find customers.</Text>
            ) : query.isLoading ? (
                <Spinner size={16} />
            ) : rows.length === 0 ? (
                <Text>No customers found</Text>
            ) : (
                <Flex sx={{ flexDirection: 'column', gap: 2 }}>
                    <Text sx={{ color: 'gray.7', fontSize: 1 }}>
                        {`${total} result${total === 1 ? '' : 's'} found`}
                    </Text>
                    <Box
                        sx={{
                            borderRadius: 'sm',
                            overflowY: 'auto',
                            backgroundColor: 'background',
                            padding: 2,
                        }}
                    >
                        <Table
                            header
                            sorting
                            columns={customerColumns as any}
                            data={rows as any}
                        />
                    </Box>
                    {hasMore ? (
                        <Flex sx={{ justifyContent: 'center' }}>
                            <Button
                                variant="secondary"
                                isLoading={query.isFetching}
                                onClick={() => setTake((t) => t + PAGE_SIZE)}
                            >
                                Load more
                            </Button>
                        </Flex>
                    ) : null}
                </Flex>
            )}
        </Flex>
    );
};

export default Customers;
